/**
 * Runtime Guard System for App-Specific Client Usage
 *
 * This system enforces that each application can only use its associated client hooks:
 * - apps/mimber-admin can only use @mimber/client/mimber-admin
 * - apps/mimber-organization can only use @mimber/client/mimber-organization
 * - apps/mimber can only use @mimber/client/mimber
 *
 * Each app must call setCurrentApp() at startup to register itself.
 */

export type AppType = "mimber-admin" | "mimber-organization" | "mimber"

// Module-level variable to store the current app
let CURRENT_APP: AppType | undefined = undefined

/**
 * Set the current app type. Must be called once at app startup.
 * @param appType - The type of app (mimber-admin, mimber-organization, or mimber)
 */
export const setCurrentApp = (appType: AppType) => {
  if (CURRENT_APP !== undefined) {
    console.warn(
      `App type already set to "${CURRENT_APP}", ignoring new value "${appType}"`
    )
    return
  }
  CURRENT_APP = appType
}

/**
 * Get the current app type
 */
export const getCurrentApp = (): AppType => {
  if (CURRENT_APP === undefined) {
    throw new Error(
      "Security Error: App type not initialized. " +
        "Call setCurrentApp() at the root of your application before using any client hooks."
    )
  }
  return CURRENT_APP
}

/**
 * Guard function that throws an error if the wrong client is being used
 */
export const guardAppUsage = (allowedApp: AppType, hookName: string) => {
  const currentApp = getCurrentApp()
  if (currentApp !== allowedApp) {
    throw new Error(
      `Security Error: Hook "${hookName}" from @mimber/client/${allowedApp} ` +
        `cannot be used in ${currentApp} app. ` +
        `Use @mimber/client/${currentApp} instead.`
    )
  }
}

/**
 * Development-only guard that shows warnings instead of throwing errors
 */
export const guardAppUsageDev = (allowedApp: AppType, hookName: string) => {
  // Check for development mode across different environments
  const isDev =
    // React Native (Expo)
    // @ts-ignore - __DEV__ is available in React Native
    (typeof globalThis !== "undefined" && globalThis.__DEV__ === true) ||
    // Node.js/fallback
    (typeof process !== "undefined" && process.env.NODE_ENV === "development")

  if (isDev) {
    try {
      const currentApp = getCurrentApp()
      if (currentApp !== allowedApp) {
        console.error(
          `🚨 Development Warning: Hook "${hookName}" from @mimber/client/${allowedApp} ` +
            `should not be used in ${currentApp} app. ` +
            `Use @mimber/client/${currentApp} instead.`
        )
      }
    } catch {
      // App not initialized yet, skip warning
    }
  }
}

/**
 * Higher-order function to wrap hooks with app usage guards
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const withAppGuard = <T extends (..._args: any[]) => any>(
  hook: T,
  allowedApp: AppType,
  hookName: string
): T => {
  return ((...args: Parameters<T>) => {
    guardAppUsage(allowedApp, hookName)
    return hook(...args)
  }) as T
}

/**
 * Decorator for TypeScript classes (future use)
 */
export const AppGuard = (allowedApp: AppType) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return <T extends { new (..._args: any[]): {} }>(constructor: T) => {
    return class extends constructor {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      constructor(...args: any[]) {
        guardAppUsage(allowedApp, constructor.name)
        super(...args)
      }
    }
  }
}
